// IMPROVE Live webinars in the app (Muhammad, 6 Oct 2026; plan of record
// docs/plans/improve-live-webinars-2026-10.md in the ops repo). The trial
// pill's "IMPROVE Live" button and the booking popup share this state: the
// upcoming webinars with places left, and the company's current booking.
//
// Trial company admins only (the API refuses everyone else). The popup opens
// from the button, from the trial emails' next=improve-live link and from the
// old /improve-live address (router.js).
import Vue from "vue";
import companyAPI from "@/api/company";

// Free Trial, Free Trial Expired (still deciding) and Free Trial Extended.
const TRIAL_STATES = [1, 2, 5];

const state = Vue.observable({
  account: null,
  loaded: false,
  loading: false,
  enabled: false,
  durationMinutes: 20,
  sessions: [],
  booking: null,
  open: false
});
let pending = null;

// Read only for components; change it through the functions below.
export const webinar = state;

// The people who may book in the app: Direct trial company admins.
export function canBookWebinar(getters) {
  if (!getters) return false;
  if (process.env.VUE_APP_NAME && process.env.VUE_APP_NAME !== "direct") return false;
  return (
    Number(getters.level) === 2 &&
    TRIAL_STATES.indexOf(Number(getters.companyState)) !== -1 &&
    !getters.superAdmin &&
    !getters.partner
  );
}

// The company's booking until its webinar has ended (the server stops
// returning it then too; this covers a page left open past the end).
export function currentBooking() {
  const booking = state.booking;
  if (!booking) return null;
  return new Date(booking.ends_at).getTime() > Date.now() ? booking : null;
}

// Is there anything to show: a booking, or dates that can be booked?
export function webinarAvailable() {
  return state.loaded && (!!currentBooking() || (state.enabled && state.sessions.length > 0));
}

function apply(data) {
  state.enabled = !!data.enabled;
  state.durationMinutes = Number(data.duration_minutes) || 20;
  state.sessions = Array.isArray(data.sessions) ? data.sessions : [];
  state.booking = data.booking || null;
  state.loaded = true;
}

// Loads once per account, or again when forced (opening the popup refreshes
// the places left). Shared while a request is in flight.
export function loadWebinar(account, force) {
  if (!force && state.account === account && (state.loaded || pending)) {
    return pending || Promise.resolve(state);
  }
  if (state.account !== account) {
    state.loaded = false;
    state.booking = null;
    state.sessions = [];
  }
  state.account = account;
  state.loading = true;
  const request = companyAPI
    .getImproveLiveSessions()
    .then(function(resp) {
      if (state.account === account) apply((resp && resp.data && resp.data.data) || {});
    })
    .catch(function() {
      // Not a trial admin after all, or the API is unreachable: show nothing.
      if (state.account === account) apply({});
    })
    .then(function() {
      if (pending === request) {
        state.loading = false;
        pending = null;
      }
      return state;
    });
  pending = request;
  return request;
}

// Book a webinar, or move the company's booking to it. Rejects with the API's
// error (a full or closed date) for the popup to show.
export function bookWebinar(sessionAt) {
  return companyAPI.bookImproveLive(sessionAt).then(function(resp) {
    apply((resp && resp.data && resp.data.data) || {});
    return state.booking;
  });
}

export function cancelWebinarBooking() {
  return companyAPI.cancelImproveLiveBooking().then(function(resp) {
    apply((resp && resp.data && resp.data.data) || {});
  });
}

export function openWebinarBooking() {
  state.open = true;
}

export function closeWebinarBooking() {
  state.open = false;
}
